import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithReauth } from '../../utils/api';

// --- Type Definitions for API Payloads ---
export interface PopulationOverviewData {
  metrics: { [key: string]: number | null };
  plots: {
    ridgeline: { [batchId: string]: number[] };
    pcaScores: { pc1: number[], pc2: number[], groups: string[], subjectIds: string[], varianceExplained: number[] };
    pcaLoadings: { pc1: number[], pc2: number[], labels: string[] };
  };
}
export interface SubgroupFilters {
  ageRange: [number, number];
  bmiRange: [number, number];
  genders: string[];
  smokingStatuses: string[];
  compareBy: string;
}

export interface HistoryStage {
  stageName: string;
  globalMeanZScore: number;
  top30PercentComposition: Record<string, number>;
}

export interface PersistenceScore {
  subjectId: string;
  persistenceScore: number;
  finalZScore: number;
}

export interface CumulativeHistoryData {
  stages: HistoryStage[];
  persistence: PersistenceScore[];
}

export interface MixComparisonData {
  mixSubjectId: string;
  mixSubjectZScore: number;
  batchDistributions: Record<string, number[]>;
}

export interface BioResult {
  key: string;
  name: string;
  value: number | string;
  unit: string;
  out_of_threshold: boolean;
}

export const analysisApi = createApi({
  reducerPath: 'analysisApi',
  baseQuery: baseQueryWithReauth,
  endpoints: (builder) => ({
    getOverviewData: builder.query<PopulationOverviewData, void>({
      query: () => `/api/overview`,
    }),
    getBatchList: builder.query<string[], void>({
      query: () => `/api/batches`,
    }),
    getBatchAnalysis: builder.query<any, string>({
      query: (batchName) => `/api/batch-analysis/${batchName}`,
    }),
    performSubgroupAnalysis: builder.mutation<any, SubgroupFilters>({
      query: (filters) => ({
        url: `/api/subgroup-analysis`,
        method: 'POST',
        body: filters,
      }),
    }),
    getAllSubjects: builder.query<{ SubjectID: string }[], void>({
      query: () => `/api/subjects`,
    }),
    getSubjectDetails: builder.query<any, string>({
      query: (subjectId) => `/api/subjects/${subjectId}/details`,
    }),
    getSubjectBioResults: builder.query<BioResult[], string>({
      query: (subjectId) => `/api/subjects/${subjectId}/bio-results`,
    }),
    uploadBatchFile: builder.mutation<any, File>({
      query: (file) => {
        const formData = new FormData();
        formData.append('file', file);
        return {
          url: `/api/upload-batch`,
          method: 'POST',
          body: formData,
        };
      },
    }),
    downloadTemplate: builder.query<Blob, void>({
      query: () => ({
        url: `/api/template`,
        headers: {
          Accept:
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        },
        responseHandler: (response) => response.blob(),
      }),
    }),
    getCumulativeHistory: builder.query<CumulativeHistoryData, void>({
      query: () => `/api/history/cumulative-analysis`,
    }),
    getMixComparison: builder.query<MixComparisonData, void>({
      query: () => `/api/history/mix-comparison`,
    }),
  }),
});

export const {
  useGetOverviewDataQuery,
  useGetBatchListQuery,
  useLazyGetBatchAnalysisQuery,
  usePerformSubgroupAnalysisMutation,
  useGetAllSubjectsQuery,
  useLazyGetSubjectDetailsQuery,
  useLazyGetSubjectBioResultsQuery,
  useUploadBatchFileMutation,
  useLazyDownloadTemplateQuery,
  useGetCumulativeHistoryQuery,
  useGetMixComparisonQuery,
} = analysisApi;

